import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useSchool } from './contexts/SchoolContext';
import type { BehaviourFeatureId } from './types';

import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ModuleRoute from './components/ModuleRoute';
import { SectionGuard, SettingsIndex } from './pages/school/settings/SettingsGuards';
import { SETTINGS_SECTIONS, SETTINGS_ROOT, settingsPath, type SettingsSectionId } from './pages/school/settings/sections';

// Always-eager: tiny pages shown before auth resolves or used in every session
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import LandingPage from './pages/LandingPage';

// Lazy-loaded: split into per-role chunks so each user only downloads what they need
const PrivacyPolicyPage  = lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage   = lazy(() => import('./pages/CookiePolicyPage'));
const LegalDocumentPage  = lazy(() => import('./pages/LegalDocumentPage'));
const DataSecurityPage   = lazy(() => import('./pages/DataSecurityPage'));
const FaqPage            = lazy(() => import('./pages/FaqPage'));
const PmGuidePage        = lazy(() => import('./pages/help/pm/PmGuidePage'));

// Super admin
const AdminDashboard      = lazy(() => import('./pages/admin/AdminDashboard'));
const TrustsAdminPage     = lazy(() => import('./pages/admin/TrustsAdminPage'));
const SignInLogPage       = lazy(() => import('./pages/admin/SignInLogPage'));
const FeedbackAdminPage   = lazy(() => import('./pages/admin/FeedbackAdminPage'));

// Trust admin
const TrustDashboardPage  = lazy(() => import('./pages/trust/TrustDashboardPage'));
const ProvisionSchoolPage = lazy(() => import('./pages/admin/ProvisionSchoolPage'));
const SchoolDetailPage    = lazy(() => import('./pages/admin/SchoolDetailPage'));
const SchoolCyclePage     = lazy(() => import('./pages/admin/SchoolCyclePage'));

// School admin
const StaffRosterPage    = lazy(() => import('./pages/school/StaffRosterPage'));
const RosterPage         = lazy(() => import('./pages/school/RosterPage'));
const StudentsPage       = lazy(() => import('./pages/school/StudentsPage'));
const CalendarPage       = lazy(() => import('./pages/CalendarPage'));
const TimetablePage      = lazy(() => import('./pages/TimetablePage'));
const AttendancePage     = lazy(() => import('./pages/AttendancePage'));
const DataPackPage       = lazy(() => import('./pages/reports/DataPackPage'));
const PupilReportPage    = lazy(() => import('./pages/PupilReportPage'));
const DetentionsPage     = lazy(() => import('./pages/behaviour/DetentionsPage'));
const OnCallPage         = lazy(() => import('./pages/behaviour/OnCallPage'));
const OutOfLessonPage    = lazy(() => import('./pages/behaviour/OutOfLessonPage'));
const IsolationPage      = lazy(() => import('./pages/behaviour/IsolationPage'));
const OneWayPage         = lazy(() => import('./pages/behaviour/OneWayPage'));
const MorningLatesPage   = lazy(() => import('./pages/behaviour/MorningLatesPage'));
const BehaviourOverviewPage = lazy(() => import('./pages/behaviour/BehaviourOverviewPage'));
const BehaviourInsightsPage = lazy(() => import('./pages/behaviour/BehaviourInsightsPage'));
const OptionsManagePage  = lazy(() => import('./pages/options/OptionsManagePage'));
const PupilPortal        = lazy(() => import('./pages/options/PupilPortal'));

// Staff Absence module
const BradfordPage           = lazy(() => import('./pages/staffAbsence/BradfordPage'));
const AbsenceCalendarPage    = lazy(() => import('./pages/staffAbsence/AbsenceCalendarPage'));
const StaffMemberPage        = lazy(() => import('./pages/staffAbsence/StaffMemberPage'));
const ReviewPointsPage       = lazy(() => import('./pages/staffAbsence/ReviewPointsPage'));
const StaffAbsenceIndex      = lazy(() => import('./pages/staffAbsence/StaffAbsenceIndex'));
const ReturnToWorkPage       = lazy(() => import('./pages/staffAbsence/ReturnToWorkPage'));

// SEND & EHCP module
const SendRegisterPage = lazy(() => import('./pages/send/SendRegisterPage'));
const SendCasePage     = lazy(() => import('./pages/send/SendCasePage'));
const SendPassportsPage = lazy(() => import('./pages/send/SendPassportsPage'));
const SendPassportDetailPage = lazy(() => import('./pages/send/SendPassportDetailPage'));
const SendProvisionMapPage = lazy(() => import('./pages/send/SendProvisionMapPage'));
const MyProvisionPage = lazy(() => import('./pages/send/MyProvisionPage'));
const SendReviewsPage = lazy(() => import('./pages/send/SendReviewsPage'));
const SendContributionsPage = lazy(() => import('./pages/send/SendContributionsPage'));
const SendOversightPage = lazy(() => import('./pages/send/SendOversightPage'));
const StaffFormPage      = lazy(() => import('./pages/school/StaffFormPage'));
const StaffProfilePage   = lazy(() => import('./pages/school/StaffProfilePage'));
const OverviewPage       = lazy(() => import('./pages/school/OverviewPage'));

// Settings hub — one address for everything a school configures. The rail and
// the routes are both drawn from SETTINGS_SECTIONS (pages/school/settings/sections.ts).
const SettingsLayout           = lazy(() => import('./pages/school/settings/SettingsLayout'));
const SchoolSection            = lazy(() => import('./pages/school/settings/SchoolSection'));
const TermDatesSection         = lazy(() => import('./pages/school/settings/TermDatesSection'));
const ModulesSection           = lazy(() => import('./pages/school/settings/ModulesSection'));
const MarkingSection           = lazy(() => import('./pages/school/settings/MarkingSection'));
const WellbeingSection         = lazy(() => import('./pages/school/settings/WellbeingSection'));
const BehaviourSetupSection    = lazy(() => import('./pages/behaviour/BehaviourSetupSection'));
const LeaveSetupSection        = lazy(() => import('./pages/leave/LeaveSetupSection'));
const SendSetupSection         = lazy(() => import('./pages/send/SendSetupSection'));
const StaffAbsenceSetupSection = lazy(() => import('./pages/staffAbsence/StaffAbsenceSetupSection'));

// Shared / all roles
const HomePage        = lazy(() => import('./pages/HomePage'));
const ReviewsPage     = lazy(() => import('./pages/ReviewsPage'));
const CpdLogPage      = lazy(() => import('./pages/cpd/CpdLogPage'));
const CpdAttestationPage = lazy(() => import('./pages/cpd/CpdAttestationPage'));
const ReviewPage      = lazy(() => import('./pages/reviews/ReviewPage'));
const ReviewHistoryPage = lazy(() => import('./pages/reviews/ReviewHistoryPage'));
const ReviewIntroPage = lazy(() => import('./pages/reviews/ReviewIntroPage'));
const PrintReviewPage = lazy(() => import('./pages/reviews/PrintReviewPage'));
const CyclesPage      = lazy(() => import('./pages/CyclesPage'));
const CycleWizardPage = lazy(() => import('./pages/cycles/CycleWizardPage'));
const DocumentsPage   = lazy(() => import('./pages/DocumentsPage'));
const MyClassesPage   = lazy(() => import('./pages/MyClassesPage'));
const TodoPage        = lazy(() => import('./pages/TodoPage'));

// Leave & Cover module
const LeavePage           = lazy(() => import('./pages/leave/LeavePage'));
const LeaveApprovalsPage  = lazy(() => import('./pages/leave/LeaveApprovalsPage'));
const LeaveDashboardPage  = lazy(() => import('./pages/leave/LeaveDashboardPage'));
const LeaveCoverPage      = lazy(() => import('./pages/leave/LeaveCoverPage'));

// ParkerMarker module — self-contained route bundle
const MarkerRoutes        = lazy(() => import('./pages/marker/MarkerRoutes'));

// Policies module
const PoliciesPage        = lazy(() => import('./pages/policies/PoliciesPage'));
const PolicyDetailPage    = lazy(() => import('./pages/policies/PolicyDetailPage'));
const PolicyCompliancePage = lazy(() => import('./pages/policies/PolicyCompliancePage'));

// Assessment Insights module
const InsightsOverviewPage = lazy(() => import('./pages/insights/InsightsOverviewPage'));
const ClassComparisonPage  = lazy(() => import('./pages/insights/ClassComparisonPage'));
const InterventionsPage    = lazy(() => import('./pages/insights/InterventionsPage'));

// Wellbeing module
const WellbeingPage          = lazy(() => import('./pages/wellbeing/WellbeingPage'));
const WellbeingDashboardPage = lazy(() => import('./pages/wellbeing/WellbeingDashboardPage'));
const StudentWellbeingPage    = lazy(() => import('./pages/wellbeing/StudentWellbeingPage'));
const VoicePage              = lazy(() => import('./pages/voice/VoicePage'));
const SurveysAdminPage       = lazy(() => import('./pages/voice/SurveysAdminPage'));
const FeedbackInboxPage      = lazy(() => import('./pages/voice/FeedbackInboxPage'));

/** Full-screen spinner shown while a lazy chunk is downloading. */
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
    </div>
  );
}

/** Show landing page to unauthenticated users; redirect authenticated users by role */
function RootRedirect() {
  const { firebaseUser, appUser, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  // Signed in to Firebase but with no app profile (doc deleted / denied / not yet
  // provisioned): don't strand them on the landing page, whose only button
  // loops straight back here. Offer a clear way out.
  if (firebaseUser && !appUser) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-xl font-semibold text-gray-900">Account not set up</h1>
        <p className="max-w-md text-sm text-gray-500">
          You’re signed in, but this account isn’t linked to a school profile yet. This can happen if the
          account was just created or has been removed. Please contact your school administrator, or sign
          out and try another account.
        </p>
        <button onClick={() => { void signOut(); }} className="btn-primary text-sm">Sign out</button>
      </div>
    );
  }

  if (!appUser) return <LandingPage />;

  // Super admins manage schools; trust admins get the trust view;
  // everyone else lands on the module launcher
  if (appUser.role === 'superAdmin') return <Navigate to="/admin" replace />;
  if (appUser.role === 'trustAdmin') return <Navigate to="/trust" replace />;
  // Pupils have a single destination — the standalone options portal.
  if (appUser.role === 'student') return <Navigate to="/options" replace />;
  return <Navigate to="/home" replace />;
}

/**
 * The staff application shell. Pupils never enter it — they live entirely in the
 * separate /options portal — so any pupil who reaches a staff route is bounced
 * back there. This is the single gate that keeps the two experiences apart.
 */
function StaffShell() {
  const { appUser } = useAuth();
  if (appUser?.role === 'student') return <Navigate to="/options" replace />;
  return <Layout />;
}

/** Land on the first Behaviour & Pastoral feature the school still has switched
 *  on, so /behaviour never dead-ends on a hidden blade. */
const BEHAVIOUR_FEATURE_PATH: Record<BehaviourFeatureId, string> = {
  detentions: '/behaviour/detentions', 'on-call': '/behaviour/on-call', overview: '/behaviour/overview',
  ool: '/behaviour/out-of-lesson', isolation: '/behaviour/isolation', 'one-way': '/behaviour/one-way', lates: '/behaviour/lates',
};
function BehaviourIndex() {
  const { activeBehaviourFeatures } = useSchool();
  const first = activeBehaviourFeatures[0];
  return <Navigate to={first ? BEHAVIOUR_FEATURE_PATH[first] : '/home'} replace />;
}

/** Every section in the registry must have an element — a missing one is a type error. */
const SECTION_ELEMENTS: Record<SettingsSectionId, React.ReactElement> = {
  school: <SchoolSection />,
  'term-dates': <TermDatesSection />,
  modules: <ModulesSection />,
  behaviour: <BehaviourSetupSection />,
  leave: <LeaveSetupSection />,
  send: <SendSetupSection />,
  'staff-absence': <StaffAbsenceSetupSection />,
  marking: <MarkingSection />,
  wellbeing: <WellbeingSection />,
};

/** An address that moved. Keeps the search and #hash so deep links still land. */
function Moved({ to }: { readonly to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

export default function App() {
  const { firebaseUser, loading, linkingSso } = useAuth();
  const location = useLocation();

  // Where to send someone once they are signed in: back to the page they were
  // trying to reach (ProtectedRoute stashes it), or Home. Deep-linked approval
  // emails depend on this.
  const from = (location.state as { from?: string } | null)?.from;
  const loginRedirect = from && from.startsWith('/') && !from.startsWith('//') ? from : '/';

  // The sign-in page must survive the Microsoft popup: the popup creates a
  // session, which flips `loading` on while the profile is fetched, and this
  // spinner would replace the whole route tree — losing the page that shows
  // the server's answer. While a link is in flight, keep the tree mounted.
  if (loading && !linkingSso) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/data-security" element={<DataSecurityPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/help/performance-management/:guide?" element={<PmGuidePage />} />
      <Route
        path="/login"
        element={firebaseUser && !linkingSso ? <Navigate to={loginRedirect} replace /> : <LoginPage />}
      />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />

      {/* Pupil options portal — a standalone entry point with its own sign-in,
          separate from the staff site login. Pupils only ever see this. */}
      <Route path="/options" element={<PupilPortal />} />

      {/* Legal document viewer — standalone, no sidebar, auth required */}
      {firebaseUser && <Route path="/legal/:doc" element={<LegalDocumentPage />} />}

      {/* Authenticated staff shell (pupils are redirected to /options) */}
      <Route element={<StaffShell />}>

        {/* Module launcher — every signed-in staff user. Pupils never reach the
            staff shell (StaffShell redirects them to /options), so this is
            staff-only. */}
        <Route
          element={
            <ProtectedRoute
              roles={['superAdmin', 'schoolAdmin', 'headteacher', 'lineManager', 'staff']}
            />
          }
        >
          <Route path="/home" element={<HomePage />} />
        </Route>

        {/* Super admin */}
        <Route element={<ProtectedRoute roles={['superAdmin']} />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/schools/new" element={<ProvisionSchoolPage />} />
          <Route path="/admin/trusts" element={<TrustsAdminPage />} />
          <Route path="/admin/sign-ins" element={<SignInLogPage />} />
          <Route path="/admin/feedback" element={<FeedbackAdminPage />} />
        </Route>

        {/* Trust admin */}
        <Route element={<ProtectedRoute roles={['trustAdmin']} />}>
          <Route path="/trust" element={<TrustDashboardPage />} />
        </Route>

        {/* School detail — superAdmin (any school) or schoolAdmin (own school) */}
        <Route element={<ProtectedRoute roles={['superAdmin', 'schoolAdmin']} />}>
          <Route path="/admin/schools/:schoolId" element={<SchoolDetailPage />} />
          <Route path="/admin/schools/:schoolId/cycles/:cycleId" element={<SchoolCyclePage />} />
        </Route>

        {/* School admin */}
        <Route element={<ProtectedRoute roles={['schoolAdmin']} />}>
          {/* My School (the PM Review dashboard) retired: its staff table is the
              Staff Roster, its checklist is Home's Getting started, its legal
              card is Settings › School. */}
          <Route path="/school" element={<Moved to="/school/staff" />} />
          <Route path="/school/staff" element={<StaffRosterPage />} />
          <Route path="/school/staff/new" element={<StaffFormPage />} />
          <Route path="/school/staff/:userId" element={<StaffFormPage />} />
          <Route path="/school/staff/:userId/profile" element={<StaffProfilePage />} />
        </Route>

        {/* Settings hub — one address for everything a school configures. Open
            to every staff role only because the SEND section must stay
            reachable by a named SENCo, who is usually plain staff; SectionGuard
            narrows each section by role and module from the same registry that
            draws the rail, so the two cannot drift. */}
        <Route element={<ProtectedRoute roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']} />}>
          <Route path={SETTINGS_ROOT} element={<SettingsLayout />}>
            <Route index element={<SettingsIndex />} />
            {SETTINGS_SECTIONS.map((s) => (
              <Route key={s.id} path={s.id} element={<SectionGuard section={s}>{SECTION_ELEMENTS[s.id]}</SectionGuard>} />
            ))}
          </Route>
          {/* The setup pages' old addresses. Only signed-in staff get this far;
              the destination's guard decides the rest. */}
          <Route path="/behaviour/setup"          element={<Moved to={settingsPath('behaviour')} />} />
          <Route path="/behaviour/pastoral-setup" element={<Moved to={settingsPath('behaviour')} />} />
          <Route path="/leave/setup"              element={<Moved to={settingsPath('leave')} />} />
          <Route path="/send/setup"               element={<Moved to={settingsPath('send')} />} />
          <Route path="/staff-absence/setup"      element={<Moved to={settingsPath('staff-absence')} />} />
        </Route>

        {/* Roster — synced MIS data, readable by all school members */}
        <Route
          element={
            <ProtectedRoute
              roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']}
            />
          }
        >
          <Route path="/roster" element={<RosterPage />} />
          <Route path="/students" element={<StudentsPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/pupil/:studentId" element={<PupilReportPage />} />
        </Route>
        <Route element={<ProtectedRoute roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']} />}>
          <Route path="/timetable" element={<TimetablePage />} />
        </Route>
        <Route element={<ProtectedRoute roles={['schoolAdmin', 'headteacher', 'lineManager']} />}>
          <Route path="/attendance" element={<AttendancePage />} />
          <Route path="/reports" element={<DataPackPage />} />
        </Route>

        {/* ── Performance Management module ────────────────────────────── */}

        {/* Cycles — school admin + headteacher */}
        <Route element={<ModuleRoute module="pm" roles={['schoolAdmin', 'headteacher']} />}>
          <Route path="/cycles" element={<CyclesPage />} />
          <Route path="/cycles/:cycleId/overview" element={<OverviewPage />} />
        </Route>

        {/* Cycle wizard — school admin only */}
        <Route element={<ModuleRoute module="pm" roles={['schoolAdmin']} />}>
          <Route path="/cycles/new" element={<CycleWizardPage />} />
        </Route>

        {/* To Do + Reviews — all school members */}
        <Route element={<ModuleRoute module="pm" />}>
          <Route path="/todo" element={<TodoPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/reviews/history" element={<ReviewHistoryPage />} />
          <Route path="/reviews/:reviewId/start" element={<ReviewIntroPage />} />
          <Route path="/reviews/:reviewId" element={<ReviewPage />} />
          <Route path="/reviews/:id/print" element={<PrintReviewPage />} />
        </Route>

        {/* ── Platform-level (not module-gated) ────────────────────────── */}

        {/* Documents — all school members */}
        <Route
          element={
            <ProtectedRoute
              roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']}
            />
          }
        >
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/my-classes" element={<MyClassesPage />} />
        </Route>

        {/* ── CPD Log module — staff only (pupils never log CPD) ───────── */}
        <Route element={<ModuleRoute module="cpd" roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']} />}>
          <Route path="/cpd" element={<CpdLogPage />} />
        </Route>
        <Route element={<ModuleRoute module="cpd" roles={['schoolAdmin', 'headteacher']} />}>
          <Route path="/cpd/attestation" element={<CpdAttestationPage />} />
        </Route>

        {/* ── ParkerMarker module ──────────────────────────────────────── */}
        <Route element={<ModuleRoute module="marker" />}>
          <Route path="/marker/*" element={<MarkerRoutes />} />
        </Route>

        {/* ── Assessment Insights module — senior leadership only ──────── */}
        <Route element={<ModuleRoute module="insights" roles={['schoolAdmin', 'headteacher']} />}>
          <Route path="/insights" element={<InsightsOverviewPage />} />
          <Route path="/insights/classes" element={<ClassComparisonPage />} />
          <Route path="/insights/interventions" element={<InterventionsPage />} />
        </Route>

        {/* ── SEND & EHCP module ────────────────────────────────────────────
             The route guard is deliberately coarse: it checks entitlement and
             that the caller is staff, and nothing more. SEND access is a
             membership list in sendConfig, not a role — a SENCo is usually a
             class teacher — so the tier check happens inside the page and, for
             real, in firestore.rules. Same shape as /leave/cover. */}
        <Route element={<ModuleRoute module="send" roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']} />}>
          <Route path="/send" element={<SendRegisterPage />} />
          {/* Tier 2. The route is open to any staff member with the module —
              the case itself is authorised inside every callable, because
              access is a membership list plus this pupil's caseworkers, which
              a route guard cannot express. Someone without it gets a plain
              explanation, not a blank screen. */}
          <Route path="/send/pupil/:studentId" element={<SendCasePage />} />
          {/* Tier 0 — every teacher, for their own pupils. The rule authorises
              the listener; the page has nothing to gate. */}
          <Route path="/send/passports" element={<SendPassportsPage />} />
          <Route path="/send/passports/:studentId" element={<SendPassportDetailPage />} />
          {/* Section F. The map self-gates on the compliance tier; the tick-list
              is any staff member's own assignments, authorised by the listener. */}
          <Route path="/send/provision" element={<SendProvisionMapPage />} />
          <Route path="/send/my-provision" element={<MyProvisionPage />} />
          {/* The annual review: inbox for the compliance tier, contributions for anyone asked. */}
          <Route path="/send/reviews" element={<SendReviewsPage />} />
          <Route path="/send/contributions" element={<SendContributionsPage />} />
          <Route path="/send/oversight" element={<SendOversightPage />} />
        </Route>

        {/* ── Staff Absence module ──────────────────────────────────────────
             Coarse guard, as for SEND: entitlement and "is staff". Who may see
             a Bradford score is a list in staffAbsenceConfig; the pages gate on
             it for the explanation, and every callable enforces it for real. */}
        <Route element={<ModuleRoute module="staffAbsence" roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']} />}>
          {/* /staff-absence opens the measure the school's policy uses; both
              pages keep their own address so nothing is switched off. */}
          <Route path="/staff-absence" element={<StaffAbsenceIndex />} />
          <Route path="/staff-absence/bradford" element={<BradfordPage />} />
          <Route path="/staff-absence/review-points" element={<ReviewPointsPage />} />
          <Route path="/staff-absence/returns" element={<ReturnToWorkPage />} />
          {/* One colleague's record — audited on the server on every view. */}
          <Route path="/staff-absence/staff/:employeeId" element={<StaffMemberPage />} />
          <Route path="/staff-absence/calendar" element={<AbsenceCalendarPage />} />
        </Route>

        {/* ── Behaviour & Pastoral module — one route per feature ──────────
            Each feature is its own page/blade and self-gates on the school's
            hiddenBehaviourFeatures toggles inside the page. */}
        <Route element={<ModuleRoute module="behaviour" roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']} />}>
          <Route path="/behaviour" element={<BehaviourIndex />} />
          <Route path="/behaviour/detentions" element={<DetentionsPage />} />
          <Route path="/behaviour/on-call" element={<OnCallPage />} />
          <Route path="/behaviour/out-of-lesson" element={<OutOfLessonPage />} />
          <Route path="/behaviour/isolation" element={<IsolationPage />} />
          <Route path="/behaviour/one-way" element={<OneWayPage />} />
          <Route path="/behaviour/lates" element={<MorningLatesPage />} />
          <Route path="/behaviour/overview" element={<BehaviourOverviewPage />} />
          {/* Old combined Pastoral page → first active feature */}
          <Route path="/behaviour/pastoral" element={<BehaviourIndex />} />
        </Route>
        <Route element={<ModuleRoute module="behaviour" roles={['schoolAdmin', 'headteacher', 'lineManager']} />}>
          <Route path="/behaviour/insights" element={<BehaviourInsightsPage />} />
        </Route>
        {/* ── KS4 Options — leadership management (the pupil chooser lives in
            the standalone /options portal, outside this staff shell) ────── */}
        <Route element={<ModuleRoute module="options" roles={['schoolAdmin', 'headteacher', 'lineManager']} />}>
          <Route path="/options/manage" element={<OptionsManagePage />} />
        </Route>
        <Route element={<ModuleRoute module="wellbeing" roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']} />}>
          <Route path="/wellbeing" element={<WellbeingPage />} />
        </Route>
        <Route element={<ModuleRoute module="wellbeing" roles={['schoolAdmin', 'headteacher']} />}>
          <Route path="/wellbeing/dashboard" element={<WellbeingDashboardPage />} />
          <Route path="/wellbeing/students" element={<StudentWellbeingPage />} />
        </Route>
        {/* Voice — surveys + feedback (part of the wellbeing module), staff only */}
        <Route element={<ModuleRoute module="wellbeing" roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']} />}>
          <Route path="/voice" element={<VoicePage />} />
        </Route>
        <Route element={<ModuleRoute module="wellbeing" roles={['schoolAdmin', 'headteacher', 'lineManager']} />}>
          <Route path="/voice/surveys" element={<SurveysAdminPage />} />
          <Route path="/voice/feedback" element={<FeedbackInboxPage />} />
        </Route>

        {/* ── Policies module ──────────────────────────────────────────── */}
        <Route element={<ModuleRoute module="policies" roles={['schoolAdmin', 'headteacher', 'lineManager']} />}>
          <Route path="/policies/compliance" element={<PolicyCompliancePage />} />
        </Route>
        <Route element={<ModuleRoute module="policies" />}>
          <Route path="/policies" element={<PoliciesPage />} />
          <Route path="/policies/:policyId" element={<PolicyDetailPage />} />
        </Route>

        {/* ── Leave & Cover module — staff only (pupils never request leave) ──
             /leave/cover is NOT role-gated: a cover-team member may be plain
             staff, so membership can only be checked against leaveConfig, which
             the page itself does. */}
        <Route element={<ModuleRoute module="leave" roles={['schoolAdmin', 'headteacher', 'lineManager', 'staff']} />}>
          <Route path="/leave" element={<LeavePage />} />
          <Route path="/leave/approvals" element={<LeaveApprovalsPage />} />
          <Route path="/leave/cover" element={<LeaveCoverPage />} />
        </Route>
        <Route element={<ModuleRoute module="leave" roles={['schoolAdmin', 'headteacher']} />}>
          <Route path="/leave/dashboard" element={<LeaveDashboardPage />} />
        </Route>

      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}
